import React, { useTransition } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from './Header';
import ChatSimulation from './ChatSimulation';
import { 
  FaBuilding, 
  FaQrcode, 
  FaRobot, 
  FaChartLine,
  FaUserFriends,
  FaQuestionCircle,
  FaPlus,
  FaMinus,
  FaArrowRight,
  FaPlay
} from 'react-icons/fa';

const FeatureCard = ({ icon: Icon, title, description }) => (
  <div className="bg-gray-800/30 rounded-lg p-6 backdrop-blur-sm border border-gray-700/50 hover:border-indigo-500/50 transition-all duration-300">
    <div className="p-3 bg-indigo-500/10 rounded-lg w-fit mb-4">
      <Icon className="h-6 w-6 text-indigo-400" />
    </div>
    <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
    <p className="text-gray-400">{description}</p>
  </div>
);

const FAQ = ({ question, answer, isOpen, onToggle }) => (
  <div className="border-b border-gray-800">
    <button
      onClick={onToggle}
      className="w-full py-4 flex items-center justify-between text-left"
    >
      <span className="text-white font-medium">{question}</span>
      {isOpen ? (
        <FaMinus className="text-indigo-400" />
      ) : (
        <FaPlus className="text-indigo-400" />
      )}
    </button>
    {isOpen && (
      <div className="pb-4 text-gray-400">
        {answer}
      </div>
    )}
  </div>
);

const ShowcaseSection = ({ 
    title, 
    description,
    image,
    component, 
    imageAlt, 
    reversed,
    features = []
  }) => {

    const [firstPart, ...rest] = description.split(' ');
    const remainingText = rest.join(' ');

    return (
        <div className="py-24 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`flex flex-col ${reversed ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-12`}>
              <div className="flex-1">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  {title}
                </h2>
                <p className="text-xl mb-8">
                  <span className="text-white">{firstPart}</span>
                  <span className="text-gray-400"> {remainingText}</span>
                </p>
                {features.length > 0 && (
                  <ul className="space-y-4">
                    {features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <div className="flex-shrink-0 mt-1">
                          <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                        </div>
                        <p className="ml-4 text-gray-400">{feature}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="flex-1">
                {component ? (
                  component
                ) : (
                  <div className="relative rounded-xl overflow-hidden shadow-2xl">
                    <div className="absolute inset-0 bg-gradient-to-tr from-indigo-600/20 to-purple-600/20 mix-blend-multiply"></div>
                    <img 
                      src={image} 
                      alt={imageAlt}
                      className="w-full h-auto transform hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
  };
  
  const EnhancedHeroSection = () => {
    const [isPending, startTransition] = useTransition();
    const navigate = useNavigate();

    const handleNavigation = (path) => {
      startTransition(() => {
        navigate(path);
      });
    };

    return (
      <div className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/30 to-purple-800/30" />
          <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-gray-900" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-8">
              Transform Your Property Management with AI
              <span className="bg-gradient-to-r from-indigo-400 to-purple-400 text-transparent bg-clip-text">
                {" "}Intelligence
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto">
              Streamline guest communications, automate routine tasks, and deliver exceptional experiences. Your property management, simplified.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <button
                onClick={() => handleNavigation('/signup')}
                className="w-full sm:w-auto px-8 py-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all duration-200 transform hover:scale-105"
              >
                Start Your Free Trial
              </button>
              <button
                onClick={() => handleNavigation('#demo')}
                className="w-full sm:w-auto px-8 py-4 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-all duration-200 flex items-center justify-center gap-2"
              >
                <FaPlay className="h-4 w-4" />
                Watch Demo
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="p-6 bg-gray-800/50 rounded-lg backdrop-blur-sm">
                <h3 className="font-bold text-white mb-2">24/7 AI Assistant</h3>
                <p className="text-gray-400">Never miss a guest inquiry with our intelligent chat system</p>
              </div>
              <div className="p-6 bg-gray-800/50 rounded-lg backdrop-blur-sm">
                <h3 className="font-bold text-white mb-2">Smart Access</h3>
                <p className="text-gray-400">Secure, contactless check-ins with dynamic QR codes</p>
              </div>
              <div className="p-6 bg-gray-800/50 rounded-lg backdrop-blur-sm">
                <h3 className="font-bold text-white mb-2">Real-time Analytics</h3>
                <p className="text-gray-400">Make data-driven decisions with comprehensive insights</p>
              </div>
            </div>

              <div className="relative mt-20 mb-[-200px] z-10">
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-900/80 to-gray-900 h-[200px] translate-y-full pointer-events-none" />
                  <div className="max-w-5xl mx-auto px-4">
                      <div className="relative rounded-xl overflow-hidden shadow-2xl">
                      <img 
                          src="/images/dashboard-preview.png"
                          alt="ShortStai Dashboard Preview"
                          className="w-full h-auto transform hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-tr from-indigo-600/20 to-purple-600/20 mix-blend-multiply" />
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent" />
                      </div>
                  </div>
              </div>
          </div>
        </div>
      </div>
    );
  };
  
  // In your LandingPage component, add these showcase sections:
  const showcaseSections = [
    {
        title: "Intelligent Guest Communication",
        description: "Automate responses to guest inquiries while maintaining a personal touch. Our AI handles routine questions so you can focus on creating exceptional experiences.",
        component: <ChatSimulation />,
        imageAlt: "AI Chat Dashboard",
    },
    {
        title: "Effortless Access Management",
        description: "Streamline check-ins with our smart access system. Generate secure QR codes and manage property access with ease.",
        image: "/qr-management.png",
        imageAlt: "QR Code Management Interface",
        reversed: true,
    },
    {
      title: "Comprehensive Property Analytics",
      description: "Make informed decisions with our powerful analytics dashboard. Track performance metrics, guest satisfaction, and operational efficiency all in one place.",
      image: "/analytics-dashboard.png", // Replace with actual image
      imageAlt: "Analytics Dashboard",
    },
    {
      title: "Team Collaboration Made Simple",
      description: "Unite your property management team with our intuitive collaboration tools. Assign tasks, share updates, and maintain clear communication channels.",
      image: "/team-dashboard.png", // Replace with actual image
      imageAlt: "Team Collaboration Dashboard",
      reversed: true,
    }
  ];

const LandingPage = () => {
  const [openFAQ, setOpenFAQ] = React.useState(null);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-gray-900">
        {/* Hero Section */}
        <EnhancedHeroSection />

        {/* Features Grid */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-[200px] pb-24">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            Everything you need to manage your properties
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon={FaRobot}
              title="AI-Powered Chat"
              description="Automate guest communication with intelligent AI that understands and responds to inquiries 24/7."
            />
            <FeatureCard
              icon={FaQrcode}
              title="Smart Access"
              description="Generate QR codes for seamless guest check-in and property access management."
            />
            <FeatureCard
              icon={FaChartLine}
              title="Analytics Dashboard"
              description="Track performance metrics and guest satisfaction with detailed analytics."
            />
            <FeatureCard
              icon={FaUserFriends}
              title="Team Collaboration"
              description="Manage multiple properties with your team through role-based access control."
            />
            <FeatureCard
              icon={FaQuestionCircle}
              title="Dynamic FAQ"
              description="Create and manage property-specific FAQs that integrate with the AI chat system."
            />
            <FeatureCard
              icon={FaBuilding}
              title="Multi-Property Support"
              description="Manage multiple properties from a single dashboard with organized hierarchy."
            />
          </div>
        </div>
  
        {/* Showcase Sections */}
        {showcaseSections.map((section, index) => (
          <ShowcaseSection key={index} {...section} />
        ))}
  
        {/* Wrapper for CTA and FAQ with gradient */}
        <div className="relative bg-gradient-to-b from-gray-900 via-gray-900 to-black">
          {/* CTA Section */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <div className="bg-indigo-600/20 rounded-2xl p-8 md:p-12 backdrop-blur-sm border border-indigo-500/20">
              <div className="text-center max-w-3xl mx-auto">
                <h2 className="text-3xl font-bold text-white mb-4">
                  Ready to transform your property management?
                </h2>
                <p className="text-gray-300 mb-8">
                  Join thousands of property managers who are already using ShortStai 
                  to streamline their operations.
                </p>
                <Link
                  to="/signup"
                  className="inline-flex items-center px-8 py-3 bg-indigo-600 
                           text-white rounded-lg hover:bg-indigo-700 
                           transition-colors duration-200"
                >
                  Get Started <FaArrowRight className="ml-2" />
                </Link>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <h2 className="text-3xl font-bold text-white text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {[
                {
                  question: "What is ShortStai?",
                  answer: "ShortStai is an AI-powered property management platform that helps you automate guest communication, manage access, and streamline operations for your properties."
                },
                {
                  question: "How does the AI chat work?",
                  answer: "Our AI chat system uses advanced natural language processing to understand and respond to guest inquiries automatically, handling common questions about check-in, amenities, and local recommendations."
                },
                {
                  question: "Is it secure?",
                  answer: "Yes, ShortStai uses industry-standard encryption and security practices to protect your data and guest information. All access is managed through secure tokens and permissions."
                }
              ].map((faq, index) => (
                <FAQ
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openFAQ === index}
                  onToggle={() => setOpenFAQ(openFAQ === index ? null : index)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-black border-t border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div>
                <h3 className="text-white font-semibold mb-4">Product</h3>
                <ul className="space-y-2">
                  <li><Link to="#" className="text-gray-400 hover:text-white">Features</Link></li>
                  <li><Link to="#" className="text-gray-400 hover:text-white">Pricing</Link></li>
                  <li><Link to="#" className="text-gray-400 hover:text-white">Documentation</Link></li>
                </ul>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-4">Company</h3>
                <ul className="space-y-2">
                  <li><Link to="#" className="text-gray-400 hover:text-white">About</Link></li>
                  <li><Link to="#" className="text-gray-400 hover:text-white">Blog</Link></li>
                  <li><Link to="#" className="text-gray-400 hover:text-white">Careers</Link></li>
                </ul>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-4">Legal</h3>
                <ul className="space-y-2">
                  <li><Link to="#" className="text-gray-400 hover:text-white">Privacy</Link></li>
                  <li><Link to="#" className="text-gray-400 hover:text-white">Terms</Link></li>
                  <li><Link to="#" className="text-gray-400 hover:text-white">Security</Link></li>
                </ul>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-4">Connect</h3>
                <ul className="space-y-2">
                  <li><Link to="#" className="text-gray-400 hover:text-white">Twitter</Link></li>
                  <li><Link to="#" className="text-gray-400 hover:text-white">LinkedIn</Link></li>
                  <li><Link to="#" className="text-gray-400 hover:text-white">GitHub</Link></li>
                </ul>
              </div>
            </div>
            <div className="mt-12 pt-8 border-t border-gray-800 text-center">
              <p className="text-gray-400">© 2024 ShortStai. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </main>
    </>
  );
};

export default LandingPage;